import { CheckCircle2 } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/components/ui/button';
import { RichHtmlView } from '@/components/rich-html-view';
import { cn } from '@/lib/utils';
import { useScrollToEnd } from '@/routes/cgu/use-scroll-to-end';

// =============================================================================
// cgu-publish-gate.tsx — Modale plein écran de LECTURE FORCÉE avant publication (S29-CGU-PUBLICATION).
// =============================================================================
// A0R-1 (OPTION 1 build) : remplace le simple ConfirmDialog de publication ADMIN par une
// surcouche modale DÉDIÉE. Avant de publier un Brouillon (geste opposable, PDF write-once,
// archivage de l'ancienne version), l'admin DOIT :
//   1. faire défiler l'INTÉGRALITÉ du contenu éditorial (scroll-to-bottom) ;
//   2. cocher l'engagement « Je confirme avoir relu intégralement ».
// Le bouton « Publier » n'est activable QU'APRÈS ces deux conditions (canPublish). Les
// effets back du publish sont STRICTEMENT INCHANGÉS : `onConfirm` branche le handler existant
// (usePublishCguVersion) — cette modale ne fait QUE gater l'accès au geste.
//
// ── DÉCISIONS ARCH ────────────────────────────────────────────────────────────────────
// 1. Le scroll-gate réutilise `useScrollToEnd` (primitive renderer-agnostique), PAS DocumentViewer :
//    DocumentViewer exige des octets PDF (useCurrentCguPdf(sha256)) ; or un Brouillon a hasPdf=false
//    et AUCUN PDF (le PDF write-once est un EFFET du publish). La lecture forcée admin porte sur le
//    `contentHtml` du Brouillon, rendu LECTURE SEULE via RichHtmlView (re-sanitizé au read, aucun
//    dangerouslySetInnerHTML, hauteur naturelle → pas de scroll imbriqué qui court-circuiterait la
//    détection). NE PAS réutiliser CguEditor/RichHtmlEditor (le contentEditable a son propre
//    overflow-auto → double scroll, reachedEnd prématuré).
// 2. Modale DÉDIÉE plein écran (et non ConfirmDialog étendu) : la zone de lecture doit exposer un ref
//    scrollable propre et occuper la hauteur disponible.
//
// ── a11y (WCAG 2.1 AA) ────────────────────────────────────────────────────────────────
//   - role="dialog" + aria-modal="true" + aria-labelledby ; Échap ferme ; focus déplacé sur la zone
//     de lecture à l'ouverture (défilement clavier immédiat), restauré au déclencheur à la fermeture ;
//   - barre de progression role="progressbar" (aria-valuenow/min/max) DOUBLÉE d'un pourcentage texte ;
//   - état « lecture terminée » doublé d'un libellé texte (jamais la couleur seule) ;
//   - zone de lecture focusable (role="region" + tabIndex=0) pour défiler sans souris.

export interface CguPublishGateProps {
  /** La modale est-elle ouverte ? Fermée = rien dans le DOM. */
  readonly open: boolean;
  /** Numéro de version affiché dans le titre (ex. « CGU-2026-07 »). */
  readonly versionNumber: string;
  /** Corps HTML SERVEUR de la version à relire (re-sanitizé au read par RichHtmlView). */
  readonly contentHtml: string;
  /** Publication en cours : verrouille le bouton (évite le double-clic). */
  readonly publishing: boolean;
  /** Confirmation : branche le handler de publication existant (effets back inchangés). */
  readonly onConfirm: () => void;
  /** Fermeture demandée (Échap, clic overlay, bouton Annuler). */
  readonly onCancel: () => void;
}

export function CguPublishGate({
  open,
  versionNumber,
  contentHtml,
  publishing,
  onConfirm,
  onCancel,
}: CguPublishGateProps): React.JSX.Element | null {
  const { ref, reachedEnd, progress } = useScrollToEnd();
  const [engagementChecked, setEngagementChecked] = React.useState(false);
  const regionRef = React.useRef<HTMLDivElement | null>(null);
  const previouslyFocused = React.useRef<HTMLElement | null>(null);
  const titleId = React.useId();
  const descId = React.useId();
  const checkboxId = React.useId();

  // Callback ref composite : alimente `useScrollToEnd` (détection) ET garde une poignée
  // locale sur la région (focus initial). Un seul élément, deux consommateurs de ref.
  const setRegion = React.useCallback(
    (el: HTMLDivElement | null): void => {
      regionRef.current = el;
      ref(el);
    },
    [ref],
  );

  // Reset de l'engagement à CHAQUE fermeture : rouvrir la modale repart d'un état vierge
  // (case décochée). `reachedEnd`/`progress` se réévaluent au remontage de la région.
  React.useEffect(() => {
    if (!open) {
      setEngagementChecked(false);
    }
  }, [open]);

  // Focus initial (zone de lecture, pour défiler au clavier) + restauration à la fermeture ;
  // Échap ferme la modale.
  React.useEffect(() => {
    if (!open) {
      return;
    }
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    regionRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCancel();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      previouslyFocused.current?.focus();
    };
  }, [open, onCancel]);

  if (!open) {
    return null;
  }

  const pct = Math.round(progress * 100);
  const canPublish = reachedEnd && engagementChecked;

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-center p-4 sm:p-6">
      {/* Overlay décoratif : ferme au clic, inerte pour les lecteurs d'écran. */}
      <div
        className="absolute inset-0 bg-foreground/50"
        aria-hidden="true"
        onClick={onCancel}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        className={cn(
          'relative z-10 flex w-full max-w-3xl flex-col gap-4 rounded-lg border border-border',
          'max-h-full bg-card p-6 text-card-foreground shadow-lg',
        )}
      >
        <div className="space-y-1">
          <h2 id={titleId} className="text-lg font-semibold tracking-tight">
            Relire avant de publier « {versionNumber} »
          </h2>
          <p id={descId} className="text-sm text-muted-foreground">
            Cette version deviendra opposable et la version publiée actuelle sera archivée.
            Faites défiler l’intégralité du contenu, puis confirmez l’avoir relu pour activer
            la publication.
          </p>
        </div>

        {/* Barre de progression : role="progressbar" DOUBLÉ du pourcentage texte (jamais couleur seule). */}
        <div className="space-y-1">
          <div
            role="progressbar"
            aria-label="Progression de lecture"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            className="h-2 w-full overflow-hidden rounded-full bg-muted"
          >
            <div
              className="h-full rounded-full bg-primary transition-[width] duration-150 ease-out"
              style={{ width: `${String(pct)}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground">Lecture : {pct} %</p>
        </div>

        {/* Zone de lecture scrollable : ref useScrollToEnd, focusable au clavier. RichHtmlView
            rend le contenu à sa hauteur NATURELLE (pas de scroll imbriqué). Contraste relevé
            (text-foreground) pour un texte juridique plein écran. */}
        <div
          ref={setRegion}
          role="region"
          aria-label={`Contenu de la version ${versionNumber}`}
          tabIndex={0}
          className="min-h-0 flex-1 overflow-y-auto rounded-md border border-border bg-muted/20 p-4 leading-relaxed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <RichHtmlView html={contentHtml} className="text-foreground" />
        </div>

        {/* État de parcours : doublé d'un libellé texte (jamais couleur seule). */}
        <p
          role="status"
          className={
            reachedEnd
              ? 'flex items-center gap-2 text-sm font-medium text-success'
              : 'flex items-center gap-2 text-sm text-muted-foreground'
          }
        >
          {reachedEnd ? (
            <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
          ) : null}
          {reachedEnd
            ? 'Lecture terminée. Vous pouvez confirmer et publier.'
            : 'Faites défiler jusqu’en bas pour activer la confirmation.'}
        </p>

        {/* Engagement : activable UNIQUEMENT après lecture intégrale (reachedEnd). */}
        <div className="flex items-start gap-2">
          <input
            id={checkboxId}
            type="checkbox"
            className="mt-1 h-4 w-4 rounded border-input disabled:cursor-not-allowed disabled:opacity-60"
            checked={engagementChecked}
            disabled={!reachedEnd}
            onChange={(e) => setEngagementChecked(e.target.checked)}
          />
          <label
            htmlFor={checkboxId}
            className={cn(
              'text-sm leading-snug',
              reachedEnd ? 'text-foreground' : 'text-muted-foreground',
            )}
          >
            Je confirme avoir relu intégralement cette version des CGU.
          </label>
        </div>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onCancel}>
            Annuler
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={!canPublish || publishing}
          >
            {publishing ? 'Publication…' : 'Publier'}
          </Button>
        </div>
      </div>
    </div>
  );
}
